import React, { PureComponent } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { dimensions } from 'theme/constants';

import * as applicantMessengerActions from 'actions/applicantMessenger';

import * as applicantMessengerSelectors from 'selectors/applicantMessenger';
import * as hiringSelectors from 'selectors/hiring';
import * as sessionSelectors from 'selectors/session';

import { HIRING_V2_URLS } from 'features/hiringV2/constants';
import { toggleMessengerShown } from 'features/messengerv2/actions';
import { MessageDrawerListHeader } from 'features/messengerv2/MessageDrawerList';

import Box from 'components/Box';
import LoadingIndicator from 'components/LoadingIndicator';
import MessageList from 'components/messenger/MessageList';

import cxHelpers from 'util/className';
import { toI18n } from 'util/i18n';
import { fullName } from 'util/user';

import { LAST_MESSAGE_PILL } from '../../../hiring/constants';
import ApplicantMessengerMessageForm from '../ApplicantMessengerMessageForm';
import ApplicantMessengerPrefillPills from '../ApplicantMessengerPrefillPills';

@connect(
  (state, props) => ({
    application: applicantMessengerSelectors.getCurrentApplication(
      state,
      props
    ),
    conversationId: applicantMessengerSelectors.getCurrentConversationId(
      state,
      props
    ),
    currentCompanyId: sessionSelectors.getCurrentCompanyId(state, props),
    currentUser: sessionSelectors.getCurrentUser(state, props),
    fetchingMore:
      applicantMessengerSelectors.getIsFetchingMoreMessagesForApplication(
        state,
        props
      ),
    loading:
      applicantMessengerSelectors.getIsFetchingInitialMessagesForApplication(
        state,
        props
      ),
    applicationLoading: applicantMessengerSelectors.getApplicationLoading(
      state,
      props
    ),
    useLinkInHeader: applicantMessengerSelectors.getUseLinkInHeader(
      state,
      props
    ),
    messages: applicantMessengerSelectors.getCurrentMessages(state, props),
    owner: hiringSelectors.getManageApplicantsOwner(state, props),
    prefillLastMessage: hiringSelectors.getPrefillLastMessage(state, props),
    isHiringV2Enabled: !sessionSelectors.getHiringV1Enabled(state),
  }),
  {
    hideApplicantMessenger: applicantMessengerActions.hideApplicantMessenger,
    receiveNewMessage: applicantMessengerActions.receiveNewMessage,
    fetchConversationMessages:
      applicantMessengerActions.fetchConversationMessages,
    sendMessage: applicantMessengerActions.sendMessage,
    readMessage: applicantMessengerActions.readMessage,
    toggleMessenger: toggleMessengerShown,
  }
)
@cxHelpers('ApplicantMessengerView')
export default class ApplicantMessengerView extends PureComponent {
  static propTypes = {
    application: ImmutablePropTypes.map.isRequired,
    conversationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currentCompanyId: PropTypes.number.isRequired,
    currentUser: ImmutablePropTypes.map.isRequired,
    fetchConversationMessages: PropTypes.func.isRequired,
    onReturn: PropTypes.func,
    fetchingMore: PropTypes.bool.isRequired,
    useLinkInHeader: PropTypes.bool.isRequired,
    hideApplicantMessenger: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    applicationLoading: PropTypes.bool.isRequired,
    messages: ImmutablePropTypes.list.isRequired,
    receiveNewMessage: PropTypes.func.isRequired,
    sendMessage: PropTypes.func.isRequired,
    readMessage: PropTypes.func.isRequired,
    prefillLastMessage: ImmutablePropTypes.map,
    toggleMessenger: PropTypes.func.isRequired,
    isHiringV2Enabled: PropTypes.bool.isRequired,
  };

  state = {
    prefillMessage: null,
  };

  componentDidMount() {
    const { fetchConversationMessages, conversationId } = this.props;

    if (conversationId) fetchConversationMessages(conversationId);
  }

  // TODO: https://joinhomebase.atlassian.net/browse/HIRING-441
  UNSAFE_componentWillMount() {
    if (this.props.conversationId) {
      this.props.fetchConversationMessages(this.props.conversationId);
    }
  }

  // TODO: https://joinhomebase.atlassian.net/browse/HIRING-441
  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.conversationId &&
      this.props.conversationId !== nextProps.conversationId
    ) {
      this.props.fetchConversationMessages(nextProps.conversationId);
    }

    // If the most recent message has changed, fire readMessage to notify server that current
    // user has read it.
    if (
      nextProps.messages.size &&
      nextProps.messages.last() !== this.props.messages.last()
    ) {
      this.readLastMessage(nextProps);
    }
  }

  readLastMessage(props) {
    props.readMessage(props.conversationId, props.messages.last().get('id'));
  }

  handleClose = () => this.props.toggleMessenger();

  handleSendMessage = ({ message }) => {
    if (!message || !message.trim()) {
      return;
    }

    this.props.sendMessage(this.props.application.get('id'), message);
  };

  handleFetchMore = lastMessageId => {
    if (this.props.conversationId) {
      this.props.fetchConversationMessages(
        this.props.conversationId,
        lastMessageId
      );
    }
  };

  handlePrefillPillClick = pillId => {
    if (pillId === LAST_MESSAGE_PILL) {
      return this.handlePrefillLastMessagePillClick();
    }

    const { application, currentUser } = this.props;
    const i18nId = `hiring.application_messaging.prefill_pills.${pillId}.content`;
    const i18nProps = {
      props: {
        applicant_first_name: application.get('first_name'),
        location: application.get('location_name'),
        manager_first_name: currentUser.get('first_name'),
        manager_phone: currentUser.get('phone'),
      },
    };
    const prefillMessage = toI18n(i18nId, i18nProps);
    this.setState({ prefillMessage });
  };

  handlePrefillLastMessagePillClick = () =>
    this.setState({
      prefillMessage: this.props.prefillLastMessage.get('body'),
    });

  handleResetPrefill = () => this.setState({ prefillMessage: null });

  title = () => {
    if (this.props.applicationLoading) {
      return <LoadingIndicator />;
    } else if (this.props.useLinkInHeader) {
      return (
        <Box>
          {toI18n('hiring.application_messaging.header_prefix')}
          &nbsp;
          {fullName(this.props.application)}
        </Box>
      );
    }

    return (
      <Box>
        {toI18n('hiring.application_messaging.header_prefix')}
        &nbsp;
        {fullName(this.props.application)}
      </Box>
    );
  };

  render() {
    const {
      application,
      messages,
      conversationId,
      loading,
      applicationLoading,
      fetchingMore,
      prefillLastMessage,
    } = this.props;

    const height = `calc(100vh - ${dimensions.navHeight})`;

    const link = this.props.isHiringV2Enabled
      ? HIRING_V2_URLS.CANDIDATE_PROFILE(this.props.application.get('id'))
      : this.props.application.get('job_application_profile_path');

    const hasLastMessagePill = !conversationId && !prefillLastMessage.isEmpty();

    return (
      <Box className={this.cx()} column h={height}>
        <MessageDrawerListHeader
          title={fullName(this.props.application)}
          titleLink={link}
          handleReturn={this.props.onReturn}
          handleClose={this.handleClose}
        />

        {!applicationLoading && (
          <MessageList
            channelId={conversationId}
            customEmptyMessage={toI18n(
              'hiring.application_messaging.no_messages',
              {
                props: {
                  applicant_first_name: application.get('first_name'),
                },
              }
            )}
            messages={messages}
            isLoading={loading}
            isFetchingMore={fetchingMore}
            onFetchMore={this.handleFetchMore}
          />
        )}

        {!applicationLoading && (
          <ApplicantMessengerPrefillPills
            hasApplicantPhone={!!this.props.application.get('phone')}
            hasLastMessagePill={hasLastMessagePill}
            onPrefillPillClick={this.handlePrefillPillClick}
          />
        )}

        {!applicationLoading && (
          <ApplicantMessengerMessageForm
            onResetPrefill={this.handleResetPrefill}
            onSend={this.handleSendMessage}
            prefillMessage={this.state.prefillMessage}
          />
        )}
      </Box>
    );
  }
}
